import { loadVehicleDesign, catalogAsset } from '@vador/library';
import { Component } from '@theme/component';
import { ensureQuoteUploads } from '@vador/uploads';
import { buildQuoteContact, quoteCanSubmit, quoteUploadsReady, referenceUrl, quoteReturnPath } from '@vador/requests';
import { buildOrderProperties, resolveOrderVariant, canPurchase, buildOrnamentPricingPreview, ornamentPreviewTotal, backMessageFitsLines, applyWoodUpcharges, buildPreviewCartLine, addPreviewCartLine, validPreviewQuantity } from '@vador/ordering';
function initializeProduct(root, design) {

const config = JSON.parse(root.querySelector('[data-vador-product-config]').textContent);
if (design) { Object.assign(config.products.auto, design.product, {short:design.title}); config.orderingEnabled = false; config.hasImage = true; }
const strings = JSON.parse(root.querySelector('[data-vador-strings]').textContent);
const t = key => strings[key] || key;
const products = config.products;
const requestConfig = JSON.parse(root.querySelector('[data-vador-request-config]').textContent);
const uploadedReferences = new Set();
const catalog = JSON.parse(root.querySelector('[data-vador-catalog]').textContent);
const orderStrings = JSON.parse(root.querySelector('[data-vador-order-strings]').textContent);
// Once published to the storefront, scent names come directly from the product options.
if (catalog && products[config.active]?.scent) {
  const optionIndex = catalog.options.indexOf('Format and scent');
  if (optionIndex !== -1) {
    const labels = [...new Set(catalog.variants.map(variant => variant.options[optionIndex]))];
    config.scents = labels.filter(label => label !== 'Unscented ornament').map(label => ({value: label, label}));
  }
}
const byId = id => root.querySelector('[id="' + id + '"]');
const form = byId('personalize');
const quoteForm = byId('quote-form');
const finishConfig = JSON.parse(root.querySelector('[data-vador-finish-config]')?.textContent || '{}');
const finishStrings = finishConfig.strings || {};
const backingPriceLabel = '+' + new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:config.currency}).format(finishConfig.backingPrice ?? 5);
const finishPicker = root.querySelector('vador-finish-picker');
const readBacking = () => finishPicker?.getSelection?.() || {backing:finishPicker?.querySelector('[name="vador-backing"]:checked')?.value === 'yes',backingFinish:'',backingInk:''};
const controller = new AbortController();
const listen = (target, event, handler) => target.addEventListener(event, handler, {signal: controller.signal});
const drafts = {};
const quoteDrafts = {};
let active = null;
let quoteActive = null;
let summaryKind = 'order';
const bagStrings = JSON.parse(root.querySelector('[data-vador-bag-strings]').textContent);
const bagMode = Boolean(catalog && products[config.active]?.material && !config.orderingEnabled);
const bagMoney = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:config.currency});
let bagLines = [];
let pendingBagLine = null;
let addingToCart = false;
let uploadLoadPromise = null;
let uploadLoadState = 'idle';

function prepareQuoteUploads() {
  if (!requestConfig.enabled || !requestConfig.uploadsEnabled) return;
  const widget = byId('quote-upload-widget');
  if (widget.querySelector('input[type="file"]')) {
    uploadLoadState = widget.dataset.vadorUploadState = 'ready';
    widget.removeAttribute('aria-busy');
    byId('quote-upload-retry').hidden = true;
    byId('quote-connection-status').textContent = '';
    byId('quote-submit').disabled = false;
    fileStatus();
    return;
  }
  if (uploadLoadPromise) return;
  uploadLoadState = widget.dataset.vadorUploadState = 'loading';
  widget.setAttribute('aria-busy', 'true');
  byId('quote-submit').disabled = true;
  byId('quote-upload-retry').hidden = true;
  byId('quote-connection-status').textContent = requestConfig.strings.upload_loading;
  uploadLoadPromise = ensureQuoteUploads({container:widget, scriptUrl:requestConfig.uploadScript, signal:controller.signal})
    .then(() => {
      if (controller.signal.aborted) return;
      uploadLoadState = widget.dataset.vadorUploadState = 'ready';
      byId('quote-connection-status').textContent = '';
      byId('quote-submit').disabled = false;
      fileStatus();
    }).catch(error => {
      if (controller.signal.aborted || error.name === 'AbortError') return;
      uploadLoadState = widget.dataset.vadorUploadState = 'error';
      byId('quote-connection-status').textContent = requestConfig.strings.upload_failed;
      byId('quote-upload-retry').hidden = false;
      byId('quote-submit').disabled = true;
    }).finally(() => {
      if (!controller.signal.aborted) widget.removeAttribute('aria-busy');
      uploadLoadPromise = null;
    });
}

function renderPreviewBag() {
  const container = byId('preview-bag-lines');
  container.replaceChildren();
  if (!bagLines.length) { const empty = document.createElement('p'); empty.textContent = bagStrings.empty; container.append(empty); }
  bagLines.forEach((line, index) => {
    const article = document.createElement('article'); article.className = 'preview-bag__line';
    const heading = document.createElement('h3'); heading.textContent = line.title; article.append(heading);
    const list = document.createElement('dl');
    const details = [...Object.entries(line.properties).filter(([name]) => !name.startsWith('_')),
      [bagStrings.unit, bagMoney.format(line.unitPrice / 100)], [bagStrings.line_total, bagMoney.format(line.unitPrice * line.quantity / 100)]];
    for (const [name, value] of details) { const dt = document.createElement('dt'), dd = document.createElement('dd'); dt.textContent = name; dd.textContent = value; list.append(dt, dd); }
    article.append(list);
    const controls = document.createElement('div'); controls.className = 'preview-bag__controls';
    const label = document.createElement('label'); label.textContent = bagStrings.quantity;
    const input = document.createElement('input'); input.type = 'number'; input.value = line.quantity;
    input.min = line.quantityRule.min; input.step = line.quantityRule.increment;
    if (line.quantityRule.max != null) input.max = line.quantityRule.max;
    input.dataset.bagQuantity = index; input.setAttribute('aria-label', bagStrings.quantity + ' · ' + line.title + ' · ' + (index + 1)); label.append(input);
    const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = bagStrings.remove;
    remove.dataset.bagRemove = index; remove.setAttribute('aria-label', bagStrings.remove + ' · ' + line.title + ' · ' + (index + 1));
    controls.append(label, remove); article.append(controls); container.append(article);
  });
  byId('preview-bag-subtotal').textContent = bagMoney.format(bagLines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0) / 100);
  byId('preview-bag-count').textContent = bagLines.reduce((sum, line) => sum + line.quantity, 0);
}

function openPreviewBag() {
  renderPreviewBag(); byId('preview-bag').showModal(); byId('preview-bag-title').focus();
}

const contexts = {
  auto: {
    title: t('create_my_car_36c46c'),
    lead: t('want_your_own_car_wheels_and_modifications_b0c175'),
    help: t('show_the_whole_car_from_your_preferred_angle__92975d'),
    label: t('details_to_capture_optional_0defef'),
    placeholder: t('wheels_stance_bodywork_plate_preferences_or_o_414197')
  },
  sports: {
    title: t('create_my_sports_design_0a1b7f'),
    lead: t('have_a_photo_of_your_player_or_a_favorite_spo_924a45'),
    help: t('share_a_photo_of_your_player_an_action_shot_o_a1a506'),
    label: t('tell_us_about_your_design_99c2cb'),
    placeholder: t('the_player_making_the_catch_a_favorite_pose_j_9df474')
  },
  key: {
    title: t('create_my_key_fob_15bed4'),
    lead: t('have_a_photo_artwork_or_a_personal_idea_94aed8'),
    help: t('share_a_photo_artwork_or_reference_for_your_k_533c88'),
    label: t('tell_us_about_your_design_99c2cb'),
    placeholder: 'A person, pet, object, artwork or another idea you want to carry with you…'
  },
  idea: {
    lead: t('have_a_photo_artwork_or_a_personal_idea_94aed8'),
    title: t('create_your_own_7474aa'),
    help: t('share_a_photo_artwork_or_reference_you_can_al_e0fa0b'),
    label: t('what_would_you_like_us_to_create_7b0191'),
    placeholder: t('your_idea_the_item_you_would_like_and_the_det_4baa30')
  }
};

function populateScents(id) {
  const select = byId(id);
  select.replaceChildren(new Option(t('select_a_scent_e975c2'), ''));
  config.scents.filter(item => item.label.trim()).forEach(item => select.add(new Option(item.label, item.value)));
}
populateScents('scent');
populateScents('quote-scent');
const materials = (config.sharedPricing?.materials || []).filter((item,index,items) => item.name.trim() && items.findIndex(other => other.name.trim() === item.name.trim()) === index);
if (config.sharedPricing?.pricingModel === 'base-plus-upgrades') {
  const money = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:config.currency});
  const upgraded = applyWoodUpcharges(materials, config.sharedPricing.woodUpchargeConfig);
  materials.splice(0, materials.length, ...upgraded.map(item => ({...item, upgradeLabel:item.upgradeAmount === null ? config.sharedPricing.woodPending : item.upgradeAmount === 0 ? config.sharedPricing.woodIncluded : money.format(item.upgradeAmount)})));
  config.sharedPricing.materials = materials;
}
for (const id of ['material','quote-material']) {
  const select = byId(id);
  if (!select) continue;
  for (const material of materials) select.add(new Option([material.name, material.upgradeLabel].filter(Boolean).join(' · '), material.name));
}
const materialLabel = name => materials.find(item => item.name === name);
const defaultMaterial = materials.find(item => item.name === 'Birch Ply')?.name || ''; 
const scentLabel = value => config.scents.find(item => item.value === value)?.label || t('not_selected_183079');
const choice = name => form.querySelector('input[name="' + name + '"]:checked').value;
const value = id => byId(id).value.trim();

function conditionalField(containerId, fieldId, visible, required = visible) {
  byId(containerId).hidden = !visible;
  const field = byId(fieldId);
  field.disabled = !visible;
  field.required = visible && required;
  field.setCustomValidity('');
}

function saveDraft() {
  if (!active) return;
  drafts[active] = {
    text: byId('personal-text').value, number: byId('player-number').value,
    message: byId('short-message').value, notes: byId('design-notes').value,
    quantity: byId('quantity').value, format: choice('format'), mode: choice('text-mode'),
    material: byId('material')?.value || '', scent: byId('scent').value, back: byId('add-back').checked, backMessage: byId('back-message').value, ...readBacking()
  };
}

function syncFields() {
  const p = products[active];
  const custom = choice('text-mode') === 'custom';
  byId('custom-text-fields').hidden = !custom;
  byId('shown-note').hidden = custom;
  byId('shown-note').textContent = p.shown ? t('keeps_the_pictured_artwork_and_bd583e') + p.shown + '” wording.' : orderStrings.shown_without_text;
  byId('personal-text').disabled = !custom;
  byId('personal-text').setCustomValidity('');
  conditionalField('number-field', 'player-number', custom && p.number, false);
  conditionalField('message-field', 'short-message', custom && p.message, false);
  conditionalField('scent-note', 'scent', p.scent && choice('format') === 'Scented air freshener');
  byId('back-upgrade').hidden = !p.back;
  byId('add-back').disabled = !p.back;
  conditionalField('back-fields', 'back-message', p.back && byId('add-back').checked);
  syncOrder();
}


function syncOrder() {
  saveDraft();
  const properties = buildOrderProperties(products[active], drafts[active], config.scents);
  if (config.sharedPricing) config.sharedPricing.backingFinishes = finishPicker?.getAvailableFinishes?.() || [];
  if (drafts[active].backing) { properties[finishStrings.backing_label] = backingPriceLabel; properties[finishStrings.finish_label] = drafts[active].backingFinish || finishStrings.pending; properties[finishStrings.color_label] = finishStrings[drafts[active].backingInk] || finishStrings.pending; }
  if (catalog?.testOrdering) { properties['Production status'] = orderStrings.test_only; properties._vador_test_only = 'true'; }
  const variant = resolveOrderVariant(catalog, properties);
  const quantityRule = variant?.quantity_rule || {min: 1, increment: 1, max: null};
  byId('quantity').min = quantityRule.min;
  byId('quantity').step = quantityRule.increment;
  if (quantityRule.max == null) byId('quantity').removeAttribute('max');
  else byId('quantity').max = quantityRule.max;
  const ready = canPurchase({enabled: config.orderingEnabled && Boolean(config.sharedPricing), catalog, variant, properties, pricing: config.sharedPricing, quantity: Number(byId('quantity').value)});
  const variantField = byId('selected-variant');
  variantField.value = variant?.id || '';
  variantField.disabled = !ready;
  byId('order-properties').replaceChildren(...Object.entries(properties).map(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden'; input.name = 'properties[' + name + ']'; input.value = value; input.disabled = !ready;
    return input;
  }));
  byId('order-submit').textContent = ready ? orderStrings.add_to_cart : t('preview_order_summary_6674dc') + ' ↗';
  byId('ordering-status').textContent = ready ? (catalog?.testOrdering ? orderStrings.test_only : orderStrings.live_ordering) : orderStrings.ordering_pending;
  // Placeholder catalog prices must never appear as free retail products.
  byId('price-label').textContent = ready ? new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:config.currency}).format(variant.price / 100) : (config.sharedPricing?.pricingModel === 'base-plus-upgrades' ? config.priceLabel : (materialLabel(drafts[active].material)?.priceLabel || config.priceLabel));
  const priceParts = buildOrnamentPricingPreview(config.sharedPricing, drafts[active]);
  for (const row of root.querySelectorAll('[data-ornament-price-part]')) {
    const part = priceParts.find(item => item.key === row.dataset.ornamentPricePart);
    row.hidden = !part;
    if (part) { row.querySelector('dt').textContent = part.title; row.querySelector('dd').textContent = part.label; }
  }
  const totalRow = root.querySelector('[data-ornament-item-total]');
  if (totalRow) {
    const total = ornamentPreviewTotal(config.sharedPricing, drafts[active]);
    totalRow.hidden = !total;
    totalRow.querySelector('dd').textContent = total ? bagMoney.format(total.total / 100) : '';
    const unitRow = root.querySelector('[data-ornament-unit-price]');
    if (unitRow) {
      unitRow.hidden = !total;
      unitRow.querySelector('dd').textContent = total ? bagMoney.format(total.unitPrice / 100) : '';
    }
  }
  const pricingStatus = byId('pricing-status');
  if (pricingStatus) {
    const total = ornamentPreviewTotal(config.sharedPricing, drafts[active]);
    const message = total
      ? bagStrings.unit + ': ' + bagMoney.format(total.unitPrice / 100) + '. ' + bagStrings.line_total + ': ' + bagMoney.format(total.total / 100)
      : drafts[active].backing ? finishStrings.pending_total : config.sharedPricing.woodPending;
    // Unrelated text edits must not repeat the price announcement.
    if (pricingStatus.textContent !== message) pricingStatus.textContent = message;
  }
  return {properties, variant, ready};
}

function selectProduct(key, scroll = false) {
  saveDraft();
  active = key;
  const p = products[key];
  // Keep the server-rendered SEO title; personalization does not navigate to another product.
  byId('category').textContent = p.category;
  byId('crumb-name').textContent = p.short;
  byId('eyebrow').textContent = 'THE ' + p.category.toUpperCase() + ' COLLECTION';
  const sub = document.createElement('span');
  sub.textContent = p.subtitle;
  byId('product-title').replaceChildren(document.createTextNode(p.title), sub);
  byId('intro').textContent = p.description;
  byId('image-caption').textContent = config.publicStorefront ? 'Personalized engraving. Made for you.' : p.caption;
  byId('product-image').className = 'product-image ' + key + (config.hasImage ? ' has-media' : '');
  byId('product-image').setAttribute('aria-label', p.alt);
  byId('text-label').textContent = p.label;
  byId('personal-text').placeholder = p.placeholder;
  byId('back-message').placeholder = p.backPlaceholder;
  byId('price-label').textContent = config.priceLabel;
  byId('back-price-label').textContent = config.backPriceLabel;
  byId('format-field').hidden = !p.scent;
  byId('format-field').disabled = !p.scent;
  byId('format-step').textContent = String(1 + Number(p.material) + Number(Boolean(finishPicker))).padStart(2, '0');
  byId('step-number').textContent = String(1 + Number(p.scent) + Number(p.material) + Number(Boolean(finishPicker))).padStart(2, '0');
  byId('engraving-step').textContent = String(2 + Number(p.scent) + Number(p.material) + Number(Boolean(finishPicker))).padStart(2, '0');

  const d = drafts[key] || {};
  for (const [id, field] of Object.entries({
    'personal-text': 'text', 'player-number': 'number', 'short-message': 'message',
    'design-notes': 'notes', 'scent': 'scent', 'back-message': 'backMessage'
  })) byId(id).value = d[field] || '';
  byId('quantity').value = d.quantity || '1';
  if (byId('material')) byId('material').value = d.material ?? defaultMaterial;
  byId('add-back').checked = Boolean(d.back);
  for (const [name, selected] of [['format', d.format || 'Unscented ornament'], ['text-mode', d.mode || 'shown']]) {
    form.querySelectorAll('input[name="' + name + '"]').forEach(radio => radio.checked = radio.value === selected);
  }
  root.querySelectorAll('nav [data-product]').forEach(button => {
    button.setAttribute('aria-pressed', String(button.dataset.product === key));
  });
  finishPicker?.setSelection?.(d);
  const action = document.createElement('span');
  const requestContext = contexts[config.quoteContext || key];
  action.textContent = requestContext.title + t('get_a_quote_e8b4aa');
  byId('custom-request').replaceChildren(document.createTextNode(requestContext.lead), action);
  syncFields();
  if (scroll) {
    byId('product-title').focus({preventScroll: true});
    root.querySelector('.breadcrumb').scrollIntoView({behavior: 'instant'});
  }
}

function writeRoute(key, request = false, replace = false) {
  // File previews are supported without relying on file:// history permissions.
  if (!/^https?:$/.test(location.protocol)) return;
  const url = new URL(location.href);
  url.searchParams.set('product', key);
  if (request) url.searchParams.set('request', 'custom');
  else url.searchParams.delete('request');
  history[replace ? 'replaceState' : 'pushState'](null, '', url);
}

function showSummary(title, rows, paragraphs, kind) {
  summaryKind = kind;
  byId('preview-bag-add').hidden = !bagMode || kind !== 'order';
  byId('preview-bag-add').disabled = !pendingBagLine;
  byId('preview-bag-add-status').textContent = bagMode && kind === 'order' && !pendingBagLine ? bagStrings.pending : '';
  byId('dialog-title').textContent = title;
  byId('dialog-content').replaceChildren();
  const dl = document.createElement('dl');
  rows.forEach(([name, text]) => {
    const dt = document.createElement('dt');
    const dd = document.createElement('dd');
    dt.textContent = name;
    dd.textContent = text;
    if ([t('back_message_0c9a5a'), t('requested_back_message_e19213')].includes(name)) dd.style.whiteSpace = 'pre-wrap';
    dl.append(dt, dd);
  });
  byId('dialog-content').append(dl);
  paragraphs.forEach(text => {
    const p = document.createElement('p');
    p.textContent = text;
    byId('dialog-content').append(p);
  });
  byId('review-dialog').querySelector('.close-bottom').textContent = kind === 'quote' ? t('edit_my_request_8d776d') : t('back_to_the_design_dd81a9');
  byId('review-dialog').showModal();
  byId('dialog-title').focus();
}

function requireNonblank(id, message) {
  if (value(id)) return true;
  byId(id).setCustomValidity(message);
  byId(id).reportValidity();
  return false;
}

function validateBackLines(id, automotive) {
  if (!automotive || backMessageFitsLines(byId(id).value)) return true;
  byId(id).setCustomValidity(config.backMessagePolicy.error);
  byId(id).reportValidity();
  return false;
}

listen(form, 'submit', async event => {
  event.preventDefault();
  if (addingToCart) return;
  saveDraft();
  const p = products[active], d = drafts[active];
  if (p.material && !materialLabel(d.material)) { byId('material').setCustomValidity(config.sharedPricing.materialRequired); byId('material').reportValidity(); return; }
  if (d.mode === 'custom' && ![d.text, p.number ? d.number : '', p.message ? d.message : ''].some(text => text.trim())) {
    byId('personal-text').setCustomValidity(t('add_the_text_or_number_you_would_like_engrave_37998b'));
    byId('personal-text').reportValidity();
    return;
  }
  if (p.back && d.back && !requireNonblank('back-message', t('enter_the_message_for_the_back_or_remove_the__179910'))) return;
  if (p.back && d.back && !validateBackLines('back-message', Boolean(config.sharedPricing))) return;
  const order = syncOrder();
  if (order.ready) {
    addingToCart = true;
    const submit = byId('order-submit');
    submit.disabled = true; submit.setAttribute('aria-busy', 'true');
    byId('ordering-status').textContent = orderStrings.cart_adding;
    try {
      // Shopify validates stock and prices; only the selected variant and its properties are sent.
      const response = await fetch(form.action + '.js', {method:'POST', body:new FormData(form),
        headers:{Accept:'application/json'}, signal:controller.signal});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : orderStrings.cart_error);
      if (!controller.signal.aborted) location.assign(form.elements.return_to.value);
    } catch (error) {
      if (error.name !== 'AbortError') byId('ordering-status').textContent = error.message || orderStrings.cart_error;
    } finally {
      addingToCart = false; submit.disabled = false; submit.removeAttribute('aria-busy');
    }
    return;
  }
  const rows = [
    [t('design_59b035'), p.title + ' ' + p.subtitle],
    [t('personalization_f20345'), d.mode === 'shown' ? t('design_as_shown_5a0c81') + p.shown : (d.text.trim() || t('use_the_number_message_below_4fb1d3'))],
    [t('format_041a5d'), p.scent ? d.format : t('key_fob_29b7c1')],
    [t('quantity_44f6af'), d.quantity],
    [buildOrnamentPricingPreview(config.sharedPricing, d)[0]?.title || t('product_price_650f0f'), config.sharedPricing?.pricingModel === 'base-plus-upgrades' ? config.sharedPricing.baseLabel : (materialLabel(d.material)?.priceLabel || config.priceLabel)],
    [t('front_engraving_b34a2e'), t('included_3e2c57')]
  ];
  if (p.material) rows.splice(2, 0, [config.sharedPricing.materialTitle, d.material]);
  if (d.backing) rows.push([finishStrings.finish_label,d.backingFinish || finishStrings.pending],[finishStrings.color_label,finishStrings[d.backingInk] || finishStrings.pending]);
  if (d.mode === 'custom' && p.number && d.number.trim()) rows.push([t('number_b7baa1'), d.number.trim()]);
  if (d.mode === 'custom' && p.message && d.message.trim()) rows.push([t('short_message_or_year_12536f'), d.message.trim()]);
  if (d.notes.trim()) rows.push([t('special_instructions_97a365'), d.notes.trim()]);
  if (p.scent && d.format === 'Scented air freshener') rows.push([t('scent_0d60b7'), scentLabel(d.scent)]);
  if (p.back && d.back) {
    rows.push([t('back_message_0c9a5a'), d.backMessage.trim()]);
    if (!config.sharedPricing) rows.push([t('back_engraving_upgrade_c5a012'), t('price_pending_not_included_in_a_total_90cc57')]);
  }
  for (const part of buildOrnamentPricingPreview(config.sharedPricing, d).filter(part => part.key !== 'base')) rows.push([part.title, part.label]);
  const total = ornamentPreviewTotal(config.sharedPricing, d);
  if (config.sharedPricing?.pricingModel === 'base-plus-upgrades') {
    if (total) rows.push([bagStrings.unit, bagMoney.format(total.unitPrice / 100)], [bagStrings.line_total, bagMoney.format(total.total / 100)]);
    else rows.push([bagStrings.line_total, d.backing ? finishStrings.pending_total : config.sharedPricing.woodPending]);
  }
  pendingBagLine = bagMode ? buildPreviewCartLine({product:p,catalog,draft:d,scents:config.scents,pricing:config.sharedPricing}) : null;
  showSummary(t('your_sample_order_cb3d2c'), rows, [
    t('standard_design_pay_at_checkout_then_approve__d73ff4'),
    t('prototype_only_nothing_has_been_ordered_or_se_6f0bce'),
    ...(config.sharedPricing ? [bagStrings.extras] : []),
    ...(config.sharedPricing ? [config.sharedPricing.note, config.sharedPricing.includedNote, config.sharedPricing.packagingNote, ...(d.back ? [config.backMessagePolicy.hint] : [])].filter(Boolean) : [])
  ], 'order');
});

listen(form, 'input', syncOrder);
listen(form, 'change', syncOrder);
if (byId('material')) listen(byId('material'), 'change', () => byId('material').setCustomValidity(''));

form.querySelectorAll('input[type=radio],#add-back').forEach(input => listen(input, 'change', syncFields));
['personal-text', 'player-number', 'short-message'].forEach(id => {
  listen(byId(id), 'input', () => byId('personal-text').setCustomValidity(''));
});

// Drafts and selected files are memory-only. Closing/reloading the page clears them.
const quoteIds = ['quote-material', 'car-style', 'car-details', 'quote-details', 'quote-text', 'quote-format', 'quote-scent',
  'quote-back-message', 'quote-quantity', 'quote-name', 'quote-email'];
const quoteDefaults = Object.fromEntries(quoteIds.map(id => [id, byId(id).value]));

function saveQuote() {
  if (!quoteActive) return;
  const draft = {};
  quoteIds.forEach(id => draft[id] = byId(id).value);
  draft.back = byId('quote-add-back').checked;
  draft.files = [...byId('quote-files').files];
  quoteDrafts[quoteActive] = draft;
}

function fileStatus() {
  if (requestConfig.enabled && requestConfig.uploadsEnabled) {
    byId('file-status').textContent = uploadedReferences.size ? requestConfig.strings.reference_saved + ': ' + uploadedReferences.size : requestConfig.strings.no_references;
    byId('clear-files').hidden = true;
    return;
  }
  const files = [...byId('quote-files').files];
  byId('file-status').textContent = files.length
    ? files.map(file => file.name).join(' · ') + ' — selected locally; not uploaded.'
    : t('no_photos_selected_this_preview_does_not_uplo_fed3e4');
  byId('clear-files').hidden = files.length === 0;
}

function syncQuoteFields() {
  const scented = Boolean(products[quoteActive]?.scent) && byId('quote-format').value === 'Scented air freshener';
  conditionalField('quote-scent-wrap', 'quote-scent', scented);
  conditionalField('quote-back-fields', 'quote-back-message', byId('quote-add-back').checked);
}

function openQuote(kind) {
  if (quoteActive && quoteActive !== kind && uploadedReferences.size) {
    byId('quote-connection-status').textContent = requestConfig.strings.context_reset;
    byId('quote-dialog').showModal();
    return;
  }
  if (kind === active) saveDraft();
  const productDraft = drafts[kind] || {};
  const d = quoteDrafts[kind] || {}, context = contexts[kind];
  quoteActive = kind;
  // The app owns its upload state. Do not reset its inputs when reopening this draft.
  if (!requestConfig.enabled) quoteForm.reset();
  quoteForm.querySelectorAll('input,textarea,select').forEach(field => field.setCustomValidity(''));
  quoteIds.forEach(id => { byId(id).value = d[id] ?? (id === 'quote-back-message' ? productDraft.backMessage : undefined) ?? quoteDefaults[id]; });
  if (products[kind]?.material && d['quote-material'] === undefined) byId('quote-material').value = defaultMaterial;
  byId('quote-add-back').checked = Boolean(d.back ?? productDraft.back);
  const transfer = new DataTransfer();
  (d.files || []).forEach(file => transfer.items.add(file));
  byId('quote-files').files = transfer.files;
  fileStatus();
  byId('quote-title').textContent = context.title;
  let backingNote=quoteForm.querySelector('[data-finish-quote-note]');
  if (!backingNote && finishPicker) { backingNote=document.createElement('p');backingNote.className='field-note';backingNote.dataset.finishQuoteNote='';quoteForm.prepend(backingNote); }
  if (backingNote) { const selection=readBacking();backingNote.hidden=!selection.backing;backingNote.textContent=selection.backing?[finishStrings.quote_preference,selection.backingFinish || finishStrings.pending,finishStrings.color_label + ': ' + (finishStrings[selection.backingInk] || finishStrings.pending),backingPriceLabel].join(' · '):''; }
  const uploadsLive = requestConfig.enabled && requestConfig.uploadsEnabled;
  const returnField = quoteForm.querySelector('input[name="return_to"]');
  if (returnField) returnField.value = quoteReturnPath({pathname:location.pathname,template:config.active,kind,themeId:document.body.dataset.vadorTheme});
  byId('quote-files').hidden = uploadsLive;
  byId('quote-files').disabled = uploadsLive;
  // The app supplies its visible label; this label still names the upload group.
  byId('quote-file-label').hidden = uploadsLive;
  byId('quote-upload-widget').hidden = !uploadsLive;
  byId('clear-files').hidden = uploadsLive || byId('quote-files').files.length === 0;
  byId('quote-submit').textContent = uploadsLive ? requestConfig.strings.send : requestConfig.strings.preview + ' ↗';
  byId('quote-mode-note').textContent = uploadsLive ? requestConfig.strings.live_note : requestConfig.strings.preview_note;
  conditionalField('car-fields', 'car-details', kind === 'auto');
  conditionalField('quote-material-wrap', 'quote-material', Boolean(products[kind]?.material) && materials.length > 0, false);
  byId('quote-details').required = kind !== 'auto';
  byId('quote-details-label').textContent = context.label;
  byId('quote-details').placeholder = context.placeholder;
  byId('photo-help').textContent = context.help;
  conditionalField('quote-format-wrap', 'quote-format', Boolean(products[kind]?.scent));
  byId('quote-back-message').placeholder = products[kind]?.backPlaceholder || 'A name, message or date for the back';
  byId('quote-back-help').dataset.defaultText ||= byId('quote-back-help').textContent;
  byId('quote-back-help').textContent = products[kind]?.material ? config.backMessagePolicy.hint : byId('quote-back-help').dataset.defaultText;
  byId('quote-back-message').rows = products[kind]?.material ? 3 : 2;
  const quoteBackPrice = byId('quote-back-price');
  quoteBackPrice.dataset.defaultText ||= quoteBackPrice.textContent;
  quoteBackPrice.textContent = products[kind]?.material && config.sharedPricing?.pricingModel === 'base-plus-upgrades' ? config.sharedPricing.backLabel + ' · ' + config.sharedPricing.backTitle : quoteBackPrice.dataset.defaultText;
  syncQuoteFields();
  byId('quote-dialog').showModal();
  byId('quote-title').focus();
  if (!byId('quote-response').dataset.state) prepareQuoteUploads();
  if (byId('quote-response').dataset.state === 'received') {
    for (const child of quoteForm.children) if (child.id !== 'quote-response') child.hidden = true;
    byId('quote-response').focus();
  }
}

listen(byId('quote-upload-widget'), 'upload:added', event => {
  const file = event.detail;
  const url = referenceUrl(file?.url);
  if (!url || file?.meta?.variant === 'thumbnail') return;
  uploadedReferences.add(url);
  byId('quote-connection-status').textContent = '';
  byId('file-status').textContent = requestConfig.strings.reference_saved + ': ' + uploadedReferences.size;
});
listen(byId('quote-upload-retry'), 'click', prepareQuoteUploads);
listen(byId('quote-upload-widget'), 'upload:removed', event => {
  const url = referenceUrl(event.detail?.url);
  if (url) uploadedReferences.delete(url);
  byId('quote-connection-status').textContent = '';
  byId('file-status').textContent = uploadedReferences.size ? requestConfig.strings.reference_saved + ': ' + uploadedReferences.size : requestConfig.strings.no_references;
});
root.querySelectorAll('[data-vador-open-quote]').forEach(button => listen(button, 'click', () => openQuote(config.quoteContext || active)));
listen(byId('quote-files'), 'change', fileStatus);
listen(byId('clear-files'), 'click', () => { byId('quote-files').value = ''; fileStatus(); });
['quote-format', 'quote-add-back'].forEach(id => listen(byId(id), 'change', syncQuoteFields));
['back-message', 'car-details', 'quote-details', 'quote-back-message', 'quote-name'].forEach(id => {
  listen(byId(id), 'input', () => byId(id).setCustomValidity(''));
});

listen(byId('quote-submit'), 'click', event => {
  event.preventDefault();
  if (!quoteForm.reportValidity()) return;
  const fail = (id, message) => { const valid = requireNonblank(id, message); if (!valid) event.preventDefault(); return valid; };
  if (quoteActive === 'auto' && !fail('car-details', t('enter_the_year_make_and_model_of_your_car_447a4f'))) return;
  if (quoteActive !== 'auto' && !fail('quote-details', t('tell_us_what_you_would_like_us_to_create_4e0f4a'))) return;
  if (byId('quote-add-back').checked && !fail('quote-back-message', t('enter_your_requested_back_message_or_remove_t_a70712'))) return;
  if (byId('quote-add-back').checked && !validateBackLines('quote-back-message', Boolean(products[quoteActive]?.material))) return;
  if (!fail('quote-name', t('enter_your_name_dacddc'))) return;
  saveQuote();
  const d = quoteDrafts[quoteActive];
  const p = products[quoteActive];
  if (requestConfig.enabled) {
    const uploadWidget = byId('quote-upload-widget');
    const uploadsReady = quoteUploadsReady({
      states:[...uploadWidget.querySelectorAll('.filepond--item')].map(item => item.dataset.filepondItemState),
      referenceCount:uploadedReferences.size
    });
    const ready = quoteCanSubmit({enabled:requestConfig.enabled, uploadsEnabled:requestConfig.uploadsEnabled,
      widgetReady:uploadLoadState === 'ready' && uploadsReady && Boolean(uploadWidget.querySelector('input[type="file"]')),
      selectedLocalFiles:d.files.length, quantity:Number(d['quote-quantity'])});
    if (!ready) {
      event.preventDefault();
      byId('quote-connection-status').textContent = requestConfig.strings.upload_unavailable;
      byId('quote-connection-status').focus();
      return;
    }
    const contact = buildQuoteContact({kind:quoteActive,title:contexts[quoteActive].title,draft:d,product:p,
      scentLabel:scentLabel(d['quote-scent']),references:[...uploadedReferences]});
    const backingPreference=readBacking();
    if (backingPreference.backing) contact.body += '\n' + finishStrings.finish_label + ': ' + (backingPreference.backingFinish || finishStrings.pending) + '\n' + finishStrings.color_label + ': ' + (finishStrings[backingPreference.backingInk] || finishStrings.pending) + '\n' + finishStrings.backing_label + ': ' + backingPriceLabel;
    byId('quote-contact-body').value = contact.body;
    // Native CAPTCHA challenges render outside this form. A top-layer modal
    // would make those controls inert; retain the panel visually without it.
    const quoteDialog = byId('quote-dialog');
    quoteDialog.close();
    quoteDialog.classList.add('quote-native-flow');
    quoteDialog.show();
    // Do not bypass Shopify CAPTCHA or the uploader's own pending-file validation.
    quoteForm.requestSubmit();
    return;
  }
  event.preventDefault();
  const rows = [[t('request_4aed03'), contexts[quoteActive].title], [t('style_99a0ef'), d['car-style']]];
  if (quoteActive === 'auto') rows.push([t('vehicle_9bb28c'), d['car-details'].trim()]);
  if (p?.material && d['quote-material']) rows.push([config.sharedPricing.materialPreference, d['quote-material']]);
  const backingPreference=readBacking();if(backingPreference.backing)rows.push([finishStrings.quote_preference,backingPreference.backingFinish || finishStrings.pending],[finishStrings.color_label,finishStrings[backingPreference.backingInk] || finishStrings.pending],[finishStrings.backing_label,backingPriceLabel]);
  if (d['quote-details'].trim()) rows.push([t('details_dc3dec'), d['quote-details'].trim()]);
  if (d['quote-text'].trim()) rows.push([t('front_engraving_text_b7cb1a'), d['quote-text'].trim()]);
  rows.push([t('product_option_93a7f9'), p?.scent ? d['quote-format'] : quoteActive === 'key' ? t('key_fob_29b7c1') : t('to_be_agreed_in_your_quote_939920')]);
  if (p?.scent && d['quote-format'] === 'Scented air freshener') rows.push([t('preferred_scent_ed8a68'), scentLabel(d['quote-scent'])]);
  if (d.back) rows.push([t('requested_back_message_e19213'), d['quote-back-message'].trim()], [t('back_engraving_upgrade_c5a012'), t('to_be_priced_in_your_quote_99dbdf')]);
  rows.push(
    [t('quantity_44f6af'), d['quote-quantity']],
    [t('contact_b37456'), d['quote-name'].trim() + ' · ' + d['quote-email'].trim()],
    [t('selected_references_26c2a9'), d.files.map(file => file.name).join(', ') || t('none_selected_579894')],
    [t('price_3e8248'), t('quoted_after_review_no_payment_due_at_this_st_11544e')]
  );
  byId('quote-dialog').close();
  showSummary(t('your_sample_request_d90304'), rows, [
    t('review_quote_payment_proof_approval_productio_438475'),
    t('preview_only_your_request_and_files_have_not__19c666')
  ], 'quote');
});

root.querySelectorAll('dialog .close').forEach(button => listen(button, 'click', () => {
  if (button.closest('dialog').id === 'quote-dialog') saveQuote();
  button.closest('dialog').close();
}));
listen(byId('review-dialog').querySelector('.close-bottom'), 'click', () => {
  byId('review-dialog').close();
  if (summaryKind === 'quote') openQuote(quoteActive);
});
listen(byId('quote-dialog'), 'cancel', saveQuote);

byId('preview-bag-open').hidden = !bagMode;
listen(byId('preview-bag-open'), 'click', () => { byId('preview-bag-status').textContent = ''; openPreviewBag(); });
listen(byId('preview-bag-add'), 'click', () => {
  if (!pendingBagLine) return;
  const next = addPreviewCartLine(bagLines, pendingBagLine);
  if (!next) { byId('preview-bag-add-status').textContent = bagStrings.invalid; return; }
  bagLines = next; pendingBagLine = null;
  byId('review-dialog').close(); byId('preview-bag-status').textContent = bagStrings.added; openPreviewBag();
});
listen(byId('preview-bag-lines'), 'change', event => {
  const input = event.target.closest('[data-bag-quantity]'); if (!input) return;
  const index = Number(input.dataset.bagQuantity), line = bagLines[index], quantity = Number(input.value);
  if (!line) return;
  if (!validPreviewQuantity(line, quantity)) { input.setCustomValidity(bagStrings.invalid); input.reportValidity(); input.value = line.quantity; return; }
  input.setCustomValidity(''); bagLines = bagLines.map((entry, i) => i === index ? {...entry, quantity} : entry);
  byId('preview-bag-status').textContent = ''; renderPreviewBag();
  byId('preview-bag-lines').querySelector('[data-bag-quantity="' + index + '"]')?.focus();
});
listen(byId('preview-bag-lines'), 'input', event => event.target.setCustomValidity?.(''));
listen(byId('preview-bag-lines'), 'click', event => {
  const button = event.target.closest('[data-bag-remove]'); if (!button) return;
  const index = Number(button.dataset.bagRemove); bagLines = bagLines.filter((_, i) => i !== index);
  byId('preview-bag-status').textContent = ''; renderPreviewBag(); byId('preview-bag-title').focus();
});
listen(byId('preview-bag-continue'), 'click', () => { byId('preview-bag').close(); byId('preview-bag-open').focus(); });
listen(byId('preview-bag'), 'close', () => byId('preview-bag-open').focus());
renderPreviewBag();

function applyRoute() {
  if (byId('quote-dialog').open) { saveQuote(); byId('quote-dialog').close(); }
  if (byId('review-dialog').open) byId('review-dialog').close();
  const route = new URLSearchParams(location.search);
  const requested = config.active;
  if (requested === 'auto' && (/^\d+$/.test(route.get('make') || '') || /^[1-9]\d{0,3}$/.test(route.get('p') || ''))) {
    const back = root.querySelector('.breadcrumb a');
    const target = new URL(back.href, location.href);
    if (/^\d+$/.test(route.get('make') || '')) target.searchParams.set('make', route.get('make'));
    if (route.get('model')) target.searchParams.set('model', route.get('model').slice(0,80));
    if (route.get('body')) target.searchParams.set('body', route.get('body').slice(0,40));
    if (/^[1-9]\d{0,3}$/.test(route.get('p') || '')) target.searchParams.set('p', route.get('p'));
    if (/^\d{4}$/.test(route.get('year') || '')) target.searchParams.set('year', route.get('year'));
    for (const key of ['generation','trim']) if (route.get(key)) target.searchParams.set(key,route.get(key).slice(0,80));
    back.href = target.href;
  }
  const key = Object.hasOwn(products, requested) ? requested : 'auto';
  selectProduct(key);
  if (byId('quote-response').dataset.state) { openQuote(route.get('context') === 'idea' ? 'idea' : requested); byId('quote-response').focus(); }
  else if (route.get('request') === 'custom') {
    openQuote(route.get('context') === 'idea' ? 'idea' : requested);
    const vehicle = route.get('vehicle');
    if (requested === 'auto' && vehicle && !byId('car-details').value) byId('car-details').value = vehicle.slice(0,180);
  }
}

root.querySelectorAll('[data-vador-submit]').forEach(button => button.disabled = false);
applyRoute();
return () => controller.abort();


}
class VadorProduct extends Component {
 connectedCallback(){super.connectedCallback();this.start();}
 updatedCallback(){super.updatedCallback();this.start();}
 async start(){
   this.cleanup?.(); this.loadController?.abort(); this.loadController=new AbortController();
   if(!this.hasAttribute('data-dynamic-vehicle')){this.cleanup=initializeProduct(this);return;}
   const status=this.previousElementSibling;
   try {
     const design=await loadVehicleDesign(this,this.loadController.signal);
     if(this.loadController.signal.aborted)return;
     if (/^\/products\/[a-z0-9-]+$/.test(design.route || '')) {
       const target=new URL(design.route,location.origin);
       for(const key of ['make','model','body','year','generation','trim','p']) { const value=new URLSearchParams(location.search).get(key); if(value) target.searchParams.set(key,value); }
       location.replace(target.href);return;
     }
     const image=document.createElement('img');image.className='vador-media';image.src=catalogAsset(this,design,1000);image.srcset=[390,700,1000].map(w=>catalogAsset(this,design,w)+' '+w+'w').join(', ');image.sizes='(max-width:620px) 90vw,45vw';image.width=design.width;image.height=design.height;image.alt=design.product.alt;image.loading='eager';image.fetchPriority='high';
     const gallery=this.querySelector('#product-image');gallery.querySelectorAll('img,svg').forEach(el=>el.remove());gallery.prepend(image);
     this.cleanup=initializeProduct(this,design);this.style.visibility='';this.removeAttribute('aria-busy');status.hidden=true;
     document.title=design.title+' Ornament | Vador Laser Designs';
   }catch(error){if(!this.loadController.signal.aborted){status.textContent='';const link=document.createElement('a');link.href='/?view=vador-automotive';link.textContent=this.querySelector('[data-vehicle-error]')?.textContent||'Choose a design from Vehicles';status.append(link);}}
 }
 disconnectedCallback(){this.loadController?.abort();this.cleanup?.();super.disconnectedCallback();}
}
if(!customElements.get('vador-product-component'))customElements.define('vador-product-component',VadorProduct);
